/**
 * WriteSlide Tool — Edit slides via ReadSlideML search/replace blocks.
 *
 * The LLM reads a slide (readSlide), then outputs search/replace blocks
 * referencing the ReadSlideML XML. The backend diffs old vs new, resolves
 * def references, and executes COM operations.
 *
 * Returns: updated ReadSlideML XML + screenshot + spatial delta.
 */

import { ToolBase } from "../../../base/toolBase";
import { ToolDescription } from "../../../base/toolDescription";
import { SearchReplaceBlockSchema, WriteSlideSchema, type WriteSlideParams, type WriteSlideResult } from "./writeSlide.schema";
import { BACKEND_BASE_URL } from "../../../../../services/communication/backendClient";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import { WRITE_SLIDE_ML_REFERENCE } from "./writeSlideML.reference";
import GlobalStateManager from "../../../../globals/global-state/globalStateManager";
import type { ExcelArtifact } from "../../../linkedExcelEditor/tools/submitArtifact";
import { generateId } from "../../../../utils/idGenerator";

const STREAM_BATCH_SIZE = 1;
const STREAM_FLUSH_MS = 200;
const STREAM_LOG_PREFIX = "[WriteSlideStream]";
// Hard toggle for latency A/B testing.
// Set to true to enable start/append/commit streaming flow.
// Set to false to force single-shot writeSlide calls.
const ENABLE_WRITE_SLIDE_STREAMING = true;

type SearchReplaceBlock = WriteSlideParams["changes"][number];

const UNSAFE_CONTROL_CHARS_REGEX = /[\u0000-\u0008\u000C\u000E-\u001F\u007F-\u009F]/g; // \u000B (vertical tab) preserved — PowerPoint soft return
const LINE_SEPARATOR_REGEX = /[\u0085\u2028\u2029]/g;

function sanitizeXmlFragmentForCom(fragment: string): string {
    return fragment
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(LINE_SEPARATOR_REGEX, '\n')
        .replace(/\uFEFF/g, '')
        .replace(UNSAFE_CONTROL_CHARS_REGEX, '');
}

interface WriteSlideArtifactPayload {
    artifactId: string;
    excelId: string;
    artifactType: "chart" | "range";
    source: {
        sheetName: string;
        sourceRef: string;
        originalFilePath?: string;
    };
    oleFilePath: string;
    imageBase64?: string;
    imageFormat?: string;
    aspectRatio?: number;
    description?: string;
    createdAt?: number;
}

function extractArtifactFromContext(value: unknown): ExcelArtifact | null {
    if (!value || typeof value !== "object") return null;
    const candidate = value as Partial<ExcelArtifact>;
    if (
        typeof candidate.artifactId === "string" &&
        (candidate.artifactType === "chart" || candidate.artifactType === "range") &&
        candidate.source &&
        typeof candidate.source.sheetName === "string" &&
        typeof candidate.source.sourceRef === "string"
    ) {
        return candidate as ExcelArtifact;
    }
    const wrapped = (value as { content?: unknown }).content;
    if (wrapped && wrapped !== value) {
        return extractArtifactFromContext(wrapped);
    }
    return null;
}

function buildArtifactPayload(artifact: ExcelArtifact): WriteSlideArtifactPayload {
    return {
        artifactId: artifact.artifactId,
        excelId: artifact.excelId,
        artifactType: artifact.artifactType,
        source: {
            sheetName: artifact.source.sheetName,
            sourceRef: artifact.source.sourceRef,
            originalFilePath: artifact.source.originalFilePath,
        },
        oleFilePath: artifact.oleFilePath,
        imageBase64: artifact.imageBase64,
        imageFormat: artifact.imageFormat,
        aspectRatio: artifact.aspectRatio,
        description: artifact.description,
        createdAt: artifact.createdAt,
    };
}

function buildArtifactMetadataPayload(
    artifact: ExcelArtifact,
    insertionMode?: string
): Record<string, unknown> {
    const payload: Record<string, unknown> = {
        artifactId: artifact.artifactId,
        excelId: artifact.excelId,
        artifactType: artifact.artifactType,
        source: artifact.source,
        exportOptions: artifact.exportOptions,
        miniWorkbookPath: artifact.oleFilePath,
        insertedAt: new Date().toISOString(),
        insertionMode: insertionMode || "image",
        description: artifact.description,
    };
    if (typeof artifact.createdAt === "number") {
        payload.exportedAt = new Date(artifact.createdAt * 1000).toISOString();
    }
    return payload;
}

export class WriteSlideTool extends ToolBase<WriteSlideParams, WriteSlideResult> {
    public readonly name = "writeSlide";

    public readonly systemPromptDescription = WRITE_SLIDE_ML_REFERENCE;

    fieldsToCheck = [
        { name: "uiDescription", type: "string" as const, list_status: false, order: 0 },
        { name: "slideId", type: "string" as const, list_status: false, order: 1 },
        { name: "slideIndex", type: "number" as const, list_status: false, order: 2 },
        { name: "detailLevel", type: "string" as const, list_status: false, order: 3 },
        { name: "reReadAfterWrite", type: "boolean" as const, list_status: false, order: 4 },
        { name: "computeSpatialDelta", type: "boolean" as const, list_status: false, order: 5 },
        { name: "verbose", type: "boolean" as const, list_status: false, order: 6 },
    ];

    listsToCheck = [
        {
            name: "changes",
            type: "array" as const,
            item_type: "object" as const,
            list_status: true,
            order: 7,
            check_sub_field: "replace",
        },
    ];

    private streamId?: string;
    private streamStartPromise?: Promise<void>;
    private flushQueue: Promise<void> = Promise.resolve();
    private flushTimer: ReturnType<typeof setTimeout> | undefined;
    private bufferedChanges: SearchReplaceBlock[] = [];
    private usedStreaming = false;
    private streamError?: string;
    private streamAppendErrors: string[] = [];
    private streamingDisabledWarned = false;

    private cachedSlideNumber?: number;
    private cachedSlideIdStr?: string;
    private cachedDetailLevel: "layout" | "complete" = "complete";
    private cachedReReadAfterWrite = false;
    private cachedComputeSpatialDelta = false;
    private cachedVerbose = true;

    constructor() {
        super();
        this.description = new ToolDescription(
            this.name,
            this.systemPromptDescription,
            WriteSlideSchema,
        );
    }

    override addStep(fieldName: string, value: unknown): void {
        super.addStep(fieldName, value);

        if (fieldName === "slideIndex" && typeof value === "number") {
            this.cachedSlideNumber = value;
            if (this.bufferedChanges.length > 0) {
                this.scheduleFlush(0);
            }
            return;
        }

        if (fieldName === "slideId" && typeof value === "string") {
            this.cachedSlideIdStr = value;
            if (this.bufferedChanges.length > 0) {
                this.scheduleFlush(0);
            }
            return;
        }

        if (fieldName === "detailLevel" && typeof value === "string") {
            if (value === "layout" || value === "complete") {
                this.cachedDetailLevel = value;
            }
            return;
        }

        if (fieldName === "reReadAfterWrite" && typeof value === "boolean") {
            this.cachedReReadAfterWrite = value;
            return;
        }

        if (fieldName === "computeSpatialDelta" && typeof value === "boolean") {
            this.cachedComputeSpatialDelta = value;
            return;
        }

        if (fieldName === "verbose" && typeof value === "boolean") {
            this.cachedVerbose = value;
            return;
        }

        if (fieldName === "changes" && value && typeof value === "object") {
            if (!this.isStreamingEnabled()) {
                if (!this.streamingDisabledWarned) {
                    console.log(`${STREAM_LOG_PREFIX} Streaming disabled by settings (enableWriteSlideStreaming=false); ignoring incremental chunks`);
                    this.streamingDisabledWarned = true;
                }
                return;
            }

            const parsed = SearchReplaceBlockSchema.safeParse(value);
            if (!parsed.success) {
                console.warn(`${STREAM_LOG_PREFIX} Ignoring invalid streamed change block`);
                return;
            }

            const change = parsed.data as SearchReplaceBlock;
            if (this.isCompleteSearchReplaceBlock(change)) {
                // Normalize line endings/control chars before sending XML fragments to COM backend.
                const sanitizedChange = {
                    search: sanitizeXmlFragmentForCom(change.search),
                    replace: sanitizeXmlFragmentForCom(change.replace),
                };
                if (sanitizedChange.search !== change.search || sanitizedChange.replace !== change.replace) {
                    console.warn(
                        `[WriteSlideTool] Sanitized change before COM ` +
                        `(search ${change.search.length}→${sanitizedChange.search.length}, ` +
                        `replace ${change.replace.length}→${sanitizedChange.replace.length})`,
                    );
                }
                this.queueChange(sanitizedChange);
            } else {
                console.warn(`${STREAM_LOG_PREFIX} Ignoring partial streamed change block`);
            }
        }
    }

    private isCompleteSearchReplaceBlock(change: SearchReplaceBlock): boolean {
        const search = change.search.trim();
        const replace = change.replace.trim();
        if (!search) {
            return false;
        }

        // WriteSlideML blocks should be XML fragments copied from readSlide output.
        // Reject likely partial fragments to avoid flushing malformed streamed data.
        const isCompleteXmlLike = (fragment: string): boolean =>
            fragment.startsWith("<") && fragment.endsWith(">");

        if (!isCompleteXmlLike(search)) {
            return false;
        }

        // Empty replace is a valid "delete" operation.
        if (!replace) {
            return true;
        }

        return isCompleteXmlLike(replace);
    }

    private queueChange(change: SearchReplaceBlock): void {
        this.usedStreaming = true;
        this.bufferedChanges.push(change);

        if (this.bufferedChanges.length >= STREAM_BATCH_SIZE) {
            this.scheduleFlush(0);
        } else {
            this.scheduleFlush(STREAM_FLUSH_MS);
        }
    }

    private isStreamingEnabled(): boolean {
        return ENABLE_WRITE_SLIDE_STREAMING;
    }

    private scheduleFlush(delayMs: number): void {
        if (this.flushTimer) {
            if (delayMs === 0) {
                clearTimeout(this.flushTimer);
                this.flushTimer = undefined;
            } else {
                return;
            }
        }

        if (delayMs === 0) {
            this.flushPendingChanges();
            return;
        }

        this.flushTimer = setTimeout(() => {
            this.flushTimer = undefined;
            this.flushPendingChanges();
        }, delayMs);
    }

    private canStream(): boolean {
        return Boolean(this.cachedSlideNumber || this.cachedSlideIdStr);
    }

    private async ensureStreamStarted(): Promise<void> {
        if (this.streamStartPromise) {
            return this.streamStartPromise;
        }

        this.streamId = this.streamId ?? generateId("stream");
        console.log(`${STREAM_LOG_PREFIX} Starting stream ${this.streamId}`);
        this.streamStartPromise = this.postWriteSlideML("start").then((result) => {
            if (!result.success) {
                throw new Error(result.message || "Failed to start writeSlide stream");
            }
        });
        return this.streamStartPromise;
    }

    private flushPendingChanges(): void {
        if (this.streamError || this.bufferedChanges.length === 0) {
            return;
        }
        if (!this.canStream()) {
            return;
        }

        const toSend = this.bufferedChanges.splice(0, this.bufferedChanges.length);
        this.flushQueue = this.flushQueue.then(() => this.flushChanges(toSend));
    }

    private async flushChanges(changes: SearchReplaceBlock[]): Promise<void> {
        if (!changes.length) {
            return;
        }

        try {
            await this.ensureStreamStarted();
            console.log(`${STREAM_LOG_PREFIX} Appending ${changes.length} change(s)`);
            const result = await this.postWriteSlideML("append", changes);

            if (!result.success) {
                // Log the failure but don't abort the entire stream —
                // individual change mismatches shouldn't kill valid subsequent changes.
                const errDetail = result.errors?.join('; ') || result.message || 'unknown';
                console.warn(`${STREAM_LOG_PREFIX} Append partial failure: ${errDetail}`);
                this.streamAppendErrors.push(errDetail);
            }

            if (result.metadataOperations?.length) {
                await this.applyMetadataOperations(result.metadataOperations);
            }
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            this.streamError = errorMsg;
            console.error(`${STREAM_LOG_PREFIX} Append error: ${errorMsg}`);
        }
    }

    private async postWriteSlideML(
        mode: "single" | "start" | "append" | "commit" | "abort",
        changes: SearchReplaceBlock[] = [],
    ): Promise<WriteSlideResult> {
        const documentId = await getOrCreatedocumentId();
        const slideNumber = this.cachedSlideNumber ?? 1;
        const slideIdStr = this.cachedSlideIdStr ?? `slide_${slideNumber}`;
        const detailLevel = this.cachedDetailLevel ?? "complete";

        const { images, artifacts } =
            changes.length > 0
                ? await this.resolveContextAssetsForChanges(changes)
                : { images: {}, artifacts: {} };

        const body: Record<string, unknown> = {
            documentId,
            slideId: slideNumber,
            writeSlideId: slideIdStr,
            detailLevel,
            mode,
            streamId: this.streamId,
            reReadAfterWrite: this.cachedReReadAfterWrite,
            computeSpatialDelta: this.cachedComputeSpatialDelta,
            verbose: this.cachedVerbose,
        };

        if (changes.length > 0) {
            body.changes = changes.map(c => ({ search: c.search, replace: c.replace }));
        }

        if (Object.keys(images).length > 0) {
            body.images = images;
        }
        if (Object.keys(artifacts).length > 0) {
            body.artifacts = artifacts;
        }

        const response = await fetch(`${BACKEND_BASE_URL}/com/writeSlideML`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`${STREAM_LOG_PREFIX} Backend error: ${response.status} - ${errorText}`);
            return {
                success: false,
                message: `Backend error: ${response.status}`,
                errors: [errorText],
            };
        }

        return await response.json() as WriteSlideResult & { data?: unknown };
    }

    private async resolveContextAssetsForChanges(
        changes: SearchReplaceBlock[]
    ): Promise<{
        images: Record<string, { base64: string; mimeType: string }>;
        artifacts: Record<string, WriteSlideArtifactPayload>;
    }> {
        const images: Record<string, { base64: string; mimeType: string }> = {};
        const artifacts: Record<string, WriteSlideArtifactPayload> = {};
        const contextIdPattern = /contextId="([^"]+)"/g;
        const globalState = GlobalStateManager.getInstance();
        const resolver = globalState.contextResolver;

        for (const change of changes) {
            let match: RegExpExecArray | null;
            while ((match = contextIdPattern.exec(change.replace)) !== null) {
                const ctxId = match[1];
                if (!images[ctxId]) {
                    try {
                        const img = resolver.getImage(ctxId);
                        if (img) {
                            images[ctxId] = { base64: img.base64, mimeType: img.mimeType };
                            console.log(`${STREAM_LOG_PREFIX} Resolved image for ${ctxId} (${img.mimeType})`);
                        }
                    } catch (imgErr) {
                        console.warn(`${STREAM_LOG_PREFIX} Could not resolve image ${ctxId}:`, imgErr);
                    }
                }
                if (!artifacts[ctxId] && ctxId.startsWith("artifact_")) {
                    try {
                        const raw = globalState.getContext(ctxId);
                        const artifact = extractArtifactFromContext(raw);
                        if (artifact) {
                            artifacts[ctxId] = buildArtifactPayload(artifact);
                            console.log(`${STREAM_LOG_PREFIX} Resolved artifact payload for ${ctxId}`);
                        }
                    } catch (artifactErr) {
                        console.warn(`${STREAM_LOG_PREFIX} Could not resolve artifact ${ctxId}:`, artifactErr);
                    }
                }
            }
        }
        return { images, artifacts };
    }

    private async applyMetadataOperations(
        metadataOperations: NonNullable<WriteSlideResult["metadataOperations"]>
    ): Promise<void> {
        try {
            const documentId = await getOrCreatedocumentId();
            const slideNumber = this.cachedSlideNumber ?? 1;
            const globalState = GlobalStateManager.getInstance();
            const { applyShapeMetadata } = await import('../../../../../services/metadataService');
            const grouped = new Map<string, {
                removeEntryIds: Set<string>;
                addEntriesByContextId: Map<string, {
                    sourceContextId: string;
                    type: string;
                    payload?: Record<string, unknown>;
                    mapsTo?: Array<Record<string, unknown>>;
                }>;
            }>();

            for (const metaOp of metadataOperations) {
                let bucket = grouped.get(metaOp.shapeId);
                if (!bucket) {
                    bucket = {
                        removeEntryIds: new Set<string>(),
                        addEntriesByContextId: new Map(),
                    };
                    grouped.set(metaOp.shapeId, bucket);
                }

                if (metaOp.action === "remove") {
                    bucket.removeEntryIds.add(metaOp.sourceContextId);
                    console.log(`${STREAM_LOG_PREFIX} Metadata queued remove: ${metaOp.sourceContextId} on shape ${metaOp.shapeId}`);
                    continue;
                }

                const payload = (() => {
                    if (metaOp.payload && typeof metaOp.payload === "object") {
                        return metaOp.payload;
                    }
                    if (
                        metaOp.entryType === "artifact" ||
                        metaOp.sourceContextId.startsWith("artifact_")
                    ) {
                        const existing = extractArtifactFromContext(globalState.getContext(metaOp.sourceContextId));
                        if (existing) {
                            return buildArtifactMetadataPayload(
                                existing,
                                typeof (metaOp as { payload?: { insertionMode?: unknown } }).payload?.insertionMode === "string"
                                    ? String((metaOp as { payload?: { insertionMode?: unknown } }).payload?.insertionMode)
                                    : undefined,
                            );
                        }
                    }
                    return undefined;
                })();

                bucket.addEntriesByContextId.set(metaOp.sourceContextId, {
                    sourceContextId: metaOp.sourceContextId,
                    type: metaOp.entryType || "citation",
                    payload,
                    mapsTo: metaOp.mapsTo,
                });
                console.log(`${STREAM_LOG_PREFIX} Metadata queued ${metaOp.action}: ${metaOp.sourceContextId} on shape ${metaOp.shapeId}`);
            }

            for (const [shapeId, bucket] of grouped.entries()) {
                const removeEntryIds = Array.from(bucket.removeEntryIds);
                const addEntries = Array.from(bucket.addEntriesByContextId.values());
                if (!removeEntryIds.length && !addEntries.length) {
                    continue;
                }

                await applyShapeMetadata({
                    slideId: slideNumber,
                    shapeId,
                    documentId,
                    metadata: {
                        removeEntryIds: removeEntryIds.length ? removeEntryIds : undefined,
                        addEntries: addEntries.length ? addEntries : undefined,
                    },
                });
                console.log(
                    `${STREAM_LOG_PREFIX} Metadata applied on shape ${shapeId}: ` +
                    `${addEntries.length} add/upsert, ${removeEntryIds.length} remove`,
                );
            }
        } catch (metaErr) {
            console.warn(`${STREAM_LOG_PREFIX} Metadata operations failed (non-fatal):`, metaErr);
        }
    }

    private resetStreamingState(): void {
        this.streamId = undefined;
        this.streamStartPromise = undefined;
        this.flushQueue = Promise.resolve();
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = undefined;
        }
        this.bufferedChanges = [];
        this.usedStreaming = false;
        this.streamError = undefined;
        this.streamAppendErrors = [];
        this.streamingDisabledWarned = false;
        this.cachedSlideNumber = undefined;
        this.cachedSlideIdStr = undefined;
        this.cachedDetailLevel = "complete";
        this.cachedReReadAfterWrite = false;
        this.cachedComputeSpatialDelta = false;
        this.cachedVerbose = true;
    }

    async execute(params: unknown): Promise<WriteSlideResult> {
        const parsed = WriteSlideSchema.safeParse(params);
        if (!parsed.success) {
            return {
                success: false,
                message: `Invalid parameters: ${parsed.error.message}`,
                errors: [parsed.error.message],
            };
        }
        const {
            slideId,
            slideIndex,
            detailLevel,
            changes,
            reReadAfterWrite,
            computeSpatialDelta,
            verbose,
        } = parsed.data;

        // Resolve slide number (for COM) and slide ID string (for cache)
        const slideNumber = slideIndex ?? 1; // COM needs a number; default to 1 if only slideId provided
        const slideIdStr = slideId ?? `slide_${slideNumber}`;

        // Cache for streaming append/commit calls
        this.cachedSlideNumber = slideNumber;
        this.cachedSlideIdStr = slideIdStr;
        this.cachedDetailLevel = detailLevel;
        this.cachedReReadAfterWrite = reReadAfterWrite;
        this.cachedComputeSpatialDelta = computeSpatialDelta;
        this.cachedVerbose = verbose;

        console.log(`${STREAM_LOG_PREFIX} Editing slide ${slideNumber} (${slideIdStr}), detailLevel=${detailLevel}, ${changes.length} change(s)`);
        for (let i = 0; i < changes.length; i++) {
            const c = changes[i];
            console.log(`${STREAM_LOG_PREFIX}   change[${i}]: search=${c.search.length}ch, replace=${c.replace.length}ch`);
            console.log(`${STREAM_LOG_PREFIX}     search preview: ${c.search.substring(0, 120).replace(/\n/g, "\\n")}`);
            console.log(`${STREAM_LOG_PREFIX}     replace preview: ${c.replace.substring(0, 120).replace(/\n/g, "\\n")}`);
        }

        try {
            let result: WriteSlideResult & { data?: unknown };
            const streamingEnabled = this.isStreamingEnabled();

            if (streamingEnabled && this.usedStreaming) {
                if (this.flushTimer) {
                    clearTimeout(this.flushTimer);
                    this.flushTimer = undefined;
                }
                this.flushPendingChanges();
                await this.flushQueue;

                if (this.streamError) {
                    return {
                        success: false,
                        message: `writeSlide streaming failed: ${this.streamError}`,
                        errors: [this.streamError],
                    };
                }

                await this.ensureStreamStarted();
                console.log(`${STREAM_LOG_PREFIX} Committing stream ${this.streamId}`);
                result = await this.postWriteSlideML("commit");
            } else {
                if (!streamingEnabled) {
                    console.log(`${STREAM_LOG_PREFIX} Streaming disabled by settings; using single mode`);
                }
                // Normalize line endings/control chars before sending XML fragments to COM backend.
                const sanitizedChanges = changes.map((c, i) => {
                    const sanitizedSearch = sanitizeXmlFragmentForCom(c.search);
                    const sanitizedReplace = sanitizeXmlFragmentForCom(c.replace);
                    if (sanitizedSearch !== c.search || sanitizedReplace !== c.replace) {
                        console.warn(
                            `[WriteSlideTool] Sanitized change[${i}] before COM ` +
                            `(search ${c.search.length}→${sanitizedSearch.length}, ` +
                            `replace ${c.replace.length}→${sanitizedReplace.length})`,
                        );
                    }
                    return {
                        search: sanitizedSearch,
                        replace: sanitizedReplace,
                    };
                });
                result = await this.postWriteSlideML("single", sanitizedChanges);
            }

            console.log(`${STREAM_LOG_PREFIX} Result: ${result.message}`);

            if (result.operations) {
                console.log(`${STREAM_LOG_PREFIX} Operations: edited=${result.operations.edited}, created=${result.operations.created}, deleted=${result.operations.deleted}, failed=${result.operations.failed}`);
            }
            if (result.details?.length) {
                for (const d of result.details) {
                    console.log(`${STREAM_LOG_PREFIX}   shape ${d.shapeId}: ${d.operation} ${d.status} changes=[${d.changes?.join(',')}]${d.error ? ' error=' + d.error : ''}`);
                }
            }
            if (result.warnings?.length) {
                console.warn(`${STREAM_LOG_PREFIX} Warnings:`, result.warnings);
            }
            if (result.errors?.length) {
                console.error(`${STREAM_LOG_PREFIX} Errors:`, result.errors);
            }
            if (result.spatialDelta) {
                console.log(`${STREAM_LOG_PREFIX} SpatialDelta:`, JSON.stringify(result.spatialDelta));
            }

            // Capture screenshot and queue for next LLM turn
            try {
                const documentId = await getOrCreatedocumentId();
                const { captureAndQueueSlideStateFeedback } = await import('../../../../utils/slideStateFeedback');
                const { SlideCapture } = await import('../../../../../services/capture/slideCapture');
                const { LiveTrackingStorage } = await import('../../../../../services/tracking/liveTrackingStorage');

                const storage = new LiveTrackingStorage();
                await storage.initialize();
                const slideCapture = new SlideCapture(storage);
                await slideCapture.initialize();

                await captureAndQueueSlideStateFeedback({
                    slideIndex: slideNumber,
                    toolName: 'writeSlide',
                    toolResult: result,
                    documentId,
                    slideCapture,
                    source: 'WriteSlideTool.feedback',
                    detailLevel: detailLevel as 'layout' | 'complete',
                    customGuidance: result.spatialDelta
                        ? 'Review the spatial delta below for any new overlaps, off-slide shapes, or whitespace changes caused by your edits. Fix any issues in a follow-up writeSlide call.'
                        : undefined,
                });
                console.log(`${STREAM_LOG_PREFIX} Queued screenshot feedback for slide ${slideNumber}`);
            } catch (captureErr) {
                console.warn(`${STREAM_LOG_PREFIX} Screenshot capture failed (non-fatal):`, captureErr);
            }

            // Execute metadata operations if any (via Office.js, not COM)
            if (result.metadataOperations?.length) {
                await this.applyMetadataOperations(result.metadataOperations);
            }

            // Merge any append errors from streaming into the final result
            const allErrors = [
                ...(result.errors || []),
                ...this.streamAppendErrors,
            ];
            const allWarnings = [
                ...(result.warnings || []),
                ...(this.streamAppendErrors.length > 0
                    ? [`${this.streamAppendErrors.length} change(s) failed during streaming — include more context in search strings to make them unique`]
                    : []),
            ];

            return {
                success: result.success,
                message: result.message,
                updatedXml: result.updatedXml,
                _slideInfos: [
                    {
                        slideId: slideIdStr,
                        slideIndex: slideNumber,
                        detailLevel: detailLevel,
                        type: 'write',
                        timestamp: Date.now(),
                    },
                ],
                operations: result.operations,
                details: result.details,
                spatialDelta: result.spatialDelta,
                warnings: allWarnings.length > 0 ? allWarnings : result.warnings,
                errors: allErrors.length > 0 ? allErrors : result.errors,
            };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error(`${STREAM_LOG_PREFIX} Error:`, error);
            return {
                success: false,
                message: `writeSlide failed: ${errorMsg}`,
                errors: [errorMsg],
            };
        } finally {
            this.resetStreamingState();
        }
    }
}
